import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../../features/userSlice';
import FloatingActions from '../../components/FloatingActions';

const fmtQty = (v) => (Number(v || 0)).toFixed(2);
//const fmtTime = (iso) => (iso ? dayjs(iso).format('HH:mm') : '');
const fmtTime = (value) => {
  if (!value) return '';

  // If backend sends string like 2026-04-03T09:15:00.000Z or 2026-04-03 09:15:00
  if (typeof value === 'string') {
    const m = value.match(/(\d{2}):(\d{2})(?::\d{2})?/);
    if (m) return `${m[1]}:${m[2]}`;
  }

  // If backend sends JS Date object or something else parseable
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  return '';
};
const sumBy = (arr, sel) => arr.reduce((a, r) => a + (Number(sel(r)) || 0), 0);
const avg = (arr, sel) => (arr.length ? (arr.reduce((a, r) => a + (Number(sel(r)) || 0), 0) / arr.length) : 0);

// mark time range red if either start or end is null/undefined
const isTimeIncomplete = (row) => !row?.unloading_start_time || !row?.unloading_end_time;


export default function MyUnloadedTrucksMobile() {
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;
  const navigate = useNavigate();
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const today = new Date().toISOString().slice(0, 10);
  const [inDate, setInDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // status chips (optional; falls back to zeros if endpoint not present)
  const [status, setStatus] = useState({ fromWB: 0, qcPending: 0, underUnload: 0, orphan: 0 });

  // expansion states
  const [openKey, setOpenKey] = useState(null);           // location: 'code||name'
  const [expandMode, setExpandMode] = useState('grade');  // location pivot mode
  const [openCatKey, setOpenCatKey] = useState(null);     // category: 'cat||<p_item_catg>'
  const [expandCatMode, setExpandCatMode] = useState('supplier'); // 'supplier' | 'grade'
  const [exporting, setExporting] = useState(false);

  const authCfg = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(
        '/api/wbauto/getMyUnloadedTrucks',
        { emp_id: emp_id, date: inDate, stationIds: '' },
        authCfg
      );
      console.log("fetchdata:", resp);
      setRows(resp.data?.rows || []);
    } catch (err) {
      console.error('getMyUnloadedTrucks failed:', err?.response?.data || err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const r = await axios.post(
        '/api/wbauto/getMyUnloadingStatus',
        { emp_id: emp_id, date: inDate, stationIds: '' },
        authCfg
      );
      const s = r.data?.status || {};
      setStatus({
        fromWB: Number(s.fromWB || 0),
        qcPending: Number(s.qcPending || 0),
        underUnload: Number(s.underUnload || 0),
        orphan: Number(s.orphan || 0),
      });
    } catch {
      setStatus({ fromWB: 0, qcPending: 0, underUnload: 0, orphan: 0 });
    }
  };

  // Auto-fetch when date/token/emp_id change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await axios.post(
          '/api/wbauto/getMyUnloadedTrucks',
          { emp_id: emp_id, date: inDate, stationIds: '' },
          authCfg
        );
        if (!cancelled) setRows(resp.data?.rows || []);
      } catch (err) {
        if (!cancelled) setRows([]);
        console.error('getMyUnloadedTrucks failed:', err?.response?.data || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    fetchStatus();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inDate, token, emp_id]);

  // ---------- TOP KPI: CATEGORY-WISE ----------
  const byCategory = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const cat = r.p_item_catg || 'UNKNOWN';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(r);
    }
    return Array.from(map.entries())
      .map(([cat, items]) => {
        const trucks = items.length;
        const qty = sumBy(items, (i) => i.P_CHALLAN_WT);
        const avgMin = avg(items, (i) => i.unloading_duration_min);

        // Supplier pivot inside category
        const supplierMap = new Map();
        for (const i of items) {
          const key = i.p_Trans_name || 'Unknown';
          if (!supplierMap.has(key)) supplierMap.set(key, { supplier: key, trucks: 0, qty: 0 });
          const obj = supplierMap.get(key);
          obj.trucks += 1;
          obj.qty += Number(i.P_CHALLAN_WT || 0);
        }
        const supplier = Array.from(supplierMap.values())
          .map(v => ({ ...v, qty: Number(v.qty.toFixed(2)) }))
          .sort((a, b) => b.qty - a.qty);

        // Grade (MatCode) pivot inside category
        const gradeMap = new Map();
        for (const i of items) {
          const key = i.P_ITEM_CD || 'Unknown';
          if (!gradeMap.has(key)) gradeMap.set(key, { grade: key, itemName: i.P_Item_name, trucks: 0, qty: 0 });
          const obj = gradeMap.get(key);
          obj.trucks += 1;
          obj.qty += Number(i.P_CHALLAN_WT || 0);
        }
        const grade = Array.from(gradeMap.values())
          .map(v => ({ ...v, qty: Number(v.qty.toFixed(2)) }))
          .sort((a, b) => b.qty - a.qty);

        return { cat, trucks, qty: Number(qty.toFixed(2)), avgMin: Math.round(avgMin), pivots: { supplier, grade } };
      })
      .sort((a, b) => b.qty - a.qty);
  }, [rows]);

  // ---------- LOCATION tiles ----------
  const byLocation = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = `${r.location_code}||${r.location_name}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return Array.from(map.entries())
      .map(([key, items]) => {
        const [code, name] = key.split('||');
        const trucks = items.length;
        const qty = sumBy(items, (i) => i.P_CHALLAN_WT);
        const avgMin = avg(items, (i) => i.unloading_duration_min);

        // Grade
        const gradeMap = new Map();
        for (const i of items) {
          const gk = i.P_Item_name || 'Unknown';
          if (!gradeMap.has(gk)) gradeMap.set(gk, { grade: gk, trucks: 0, qty: 0 });
          const obj = gradeMap.get(gk);
          obj.trucks += 1;
          obj.qty += Number(i.P_CHALLAN_WT || 0);
        }
        const grade = Array.from(gradeMap.values()).map(v => ({ ...v, qty: Number(v.qty.toFixed(2)) })).sort((a, b) => b.qty - a.qty);

        // Supplier
        const suppMap = new Map();
        for (const i of items) {
          const sk = i.p_Trans_name || 'Unknown';
          if (!suppMap.has(sk)) suppMap.set(sk, { supplier: sk, trucks: 0, qty: 0 });
          const obj = suppMap.get(sk);
          obj.trucks += 1;
          obj.qty += Number(i.P_CHALLAN_WT || 0);
        }
        const supplier = Array.from(suppMap.values()).map(v => ({ ...v, qty: Number(v.qty.toFixed(2)) })).sort((a, b) => b.qty - a.qty);

        // Mines
        const mineMap = new Map();
        for (const i of items) {
          const mk = i.P_LOAD_POINT || 'Unknown';
          if (!mineMap.has(mk)) mineMap.set(mk, { mine: mk, trucks: 0, qty: 0 });
          const obj = mineMap.get(mk);
          obj.trucks += 1;
          obj.qty += Number(i.P_CHALLAN_WT || 0);
        }
        const mines = Array.from(mineMap.values()).map(v => ({ ...v, qty: Number(v.qty.toFixed(2)) })).sort((a, b) => b.qty - a.qty);

        return { code, name, trucks: trucks, qty: Number(qty.toFixed(2)), avgMin: Math.round(avgMin), items, pivots: { grade, supplier, mines } };
      })
      .sort((a, b) => b.qty - a.qty);
  }, [rows]);

  /*--
  const onDownloadXLS = async () => {
    try {
      const body = { emp_id: emp_id, date: inDate, scope: 'self', groupBy: 'material', format: 'xlsx' };
      const resp = await axios.post('/api/unloading/myday/export', body, { responseType: 'blob', ...(authCfg || {}) });
      const cd = resp.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const name = m ? m[1] : `unloading_${inDate}.xlsx`;
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export XLS failed:', e?.response?.data || e.message);
      alert('Could not download the XLS file.');
    }
  };
  --*/
  const onDownloadXLS = async () => {
    try {
      setExporting(true);

      const body = {
        date: inDate,
        scope: 'self',
        groupBy: 'material',
        format: 'xlsx'
      };

      const resp = await axios.post(
        '/api/wbauto/getUnloadingExportData',
        body,
        { responseType: 'blob', ...(authCfg || {}) }
      );

      const contentType = resp.headers['content-type'];
      if (!contentType.includes('application')) {
        throw new Error('Invalid file response');
      }

      const cd = resp.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/.exec(cd);
      const fileName = match ? match[1] : `Unloading_${inDate}.xlsx`; 

      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Download XLS failed:', err?.response?.data || err.message);
      alert('Failed to download report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const onShareWhatsApp = async () => {
    try {
      const body = { emp_id: emp_id, date: inDate, scope: 'self', groupBy: 'material', format: 'xlsx' };
      const resp = await axios.post('/api/unloading/myday/export', body, { responseType: 'blob', ...(authCfg || {}) });
      const fileName = `Unloading_${inDate}.xlsx`;
      const file = new File([resp.data], fileName, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Unloading Report', text: `Unloading report for ${inDate}` });
      } else {
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        alert('File saved. Please share it via WhatsApp from your Files app.');
      }
    } catch (e) {
      console.error('WhatsApp share failed:', e?.response?.data || e.message);
      alert('Could not prepare the XLS file.');
    }
  };

  if (!token) {
    return (
      <div className="container py-4 text-center">
        <p>You must be logged in to view this screen.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>Go to Login</button>
      </div>
    );
  }

  const catBadge = (cat) => {
    const key = String(cat || '').toUpperCase();
    if (key.includes('COL')) return { text: 'Coal', cls: 'bg-dark' };
    if (key.includes('DOLO')) return { text: 'Dolomite', cls: 'bg-info text-dark' };
    if (key.includes('IRON') || key.includes('ORE')) return { text: 'Iron Ore', cls: 'bg-danger' };
    return { text: key, cls: 'bg-secondary' };
  };

  return (
    <div ref={topRef} className="p-2" style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* Controls */}
      <div className="d-flex align-items-center gap-2 mb-2 sticky-top bg-white py-2" style={{ top: 0, zIndex: 5 }}>
        <input type="date" className="form-control" style={{ maxWidth: 160 }} value={inDate} onChange={(e) => setInDate(e.target.value)} />
        <button className="btn btn-primary" onClick={fetchData} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        <button
          className="btn btn-outline-secondary"
          onClick={onDownloadXLS}
          disabled={exporting}
        >
          {exporting ? 'Preparing…' : 'Download XLS'}
        </button>
        <button className="btn btn-outline-success" onClick={onShareWhatsApp}>WhatsApp</button>
      </div>

      {/* TOP KPI: Item Category–wise tiles (expand -> Supplier / Grade pivots) */}
      <div className="mb-2">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
          {byCategory.map((c, idx) => {
            const key = `cat||${c.cat}`;
            const isOpen = openCatKey === key;
            const badge = catBadge(c.cat);
            return (
              <div
                key={idx}
                className="border rounded p-2 bg-white"
                style={{
                  gridColumn: isOpen ? '1 / -1' : 'auto',
                  boxShadow: isOpen ? '0 0 0 2px rgba(25,135,84,.25)' : 'none'
                }}
              >
                <button
                  className="w-100 text-start btn btn-link p-0"
                  onClick={() => setOpenCatKey(isOpen ? null : key)}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span className={`badge ${badge.cls}`}>{badge.text}</span>
                    <div style={{ fontSize: '.95rem', fontWeight: 700 }}>{c.cat}</div>
                  </div>
                  <div className="d-flex justify-content-between mt-1" style={{ fontSize: '.85rem' }}>
                    <span>Trucks: <b>{c.trucks}</b></span>
                    <span>Qty: <b>{fmtQty(c.qty)}</b></span>
                    <span>Avg: <b>{c.avgMin}m</b></span>
                  </div>
                </button>

                {isOpen && (
                  <div className="mt-2">
                    {/* Mode switch for Category tiles */}
                    <div className="btn-group w-100 mb-2" role="group" aria-label="Category Pivot Mode">
                      <button className={`btn btn-sm ${expandCatMode === 'supplier' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setExpandCatMode('supplier')}>Supplier</button>
                      <button className={`btn btn-sm ${expandCatMode === 'grade' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setExpandCatMode('grade')}>Grade</button>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-sm align-middle" style={{ fontSize: '.82rem' }}>
                        <thead>
                          {expandCatMode === 'supplier' ? (
                            <tr><th>Supplier</th><th className="text-end">Trucks</th><th className="text-end">Qty</th></tr>
                          ) : (
                            <tr><th>Grade (MatCode)</th><th className="text-end">Trucks</th><th className="text-end">Qty</th></tr>
                          )}
                        </thead>
                        <tbody>
                          {(expandCatMode === 'supplier' ? c.pivots.supplier : c.pivots.grade).map((p, i) => (
                            <tr key={i}>
                              <td>
                                {expandCatMode === 'supplier' ? (
                                  p.supplier
                                ) : (
                                  <>
                                    {p.itemName && <div style={{ fontSize: '.74rem', opacity: .7 }}>{p.itemName}</div>}
                                  </>
                                )}
                              </td>
                              <td className="text-end">{p.trucks}</td>
                              <td className="text-end">{fmtQty(p.qty)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Status chips under top tiles */}
        <div className="d-flex flex-wrap gap-1 mt-2">
          <span className="badge bg-primary">WB: {status.fromWB}</span>
          <span className="badge bg-warning text-dark">QC Pending: {status.qcPending}</span>
          <span className="badge bg-info">Under Unload: {status.underUnload}</span>
          <span className="badge bg-danger">Orphan: {status.orphan}</span>
        </div>
      </div>

      {/* LOCATION tiles (unchanged), single full-width expansion */}
      <div className="mb-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
        {byLocation.map((loc, idx) => {
          const key = `${loc.code}||${loc.name}`;
          const isOpen = openKey === key;
          return (
            <div key={idx} className="border rounded p-2 bg-white"
              style={{ gridColumn: isOpen ? '1 / -1' : 'auto', boxShadow: isOpen ? '0 0 0 2px rgba(13,110,253,.25)' : 'none' }}>
              <button className="w-100 text-start btn btn-link p-0" onClick={() => setOpenKey(isOpen ? null : key)} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ fontSize: '.95rem', fontWeight: 700 }}>{loc.name}</div>
                <div className="d-flex justify-content-between mt-1" style={{ fontSize: '.85rem' }}>
                  <span>Trucks: <b>{loc.trucks}</b></span>
                  <span>Qty: <b>{fmtQty(loc.qty)}</b></span>
                  <span>Avg: <b>{loc.avgMin}m</b></span>
                </div>
                <div className="mt-1"><span className="badge bg-warning text-dark">QC Pending</span></div>
              </button>

              {isOpen && (
                <div className="mt-2">
                  <div className="btn-group w-100 mb-2" role="group" aria-label="Pivot Mode">
                    <button className={`btn btn-sm ${expandMode === 'grade' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setExpandMode('grade')}>Grade</button>
                    <button className={`btn btn-sm ${expandMode === 'supplier' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setExpandMode('supplier')}>Supplier</button>
                    <button className={`btn btn-sm ${expandMode === 'mines' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setExpandMode('mines')}>Mines</button>
                  </div>

                  <div className="table-responsive mb-2">
                    <table className="table table-sm align-middle" style={{ fontSize: '.82rem' }}>
                      <thead>
                        {expandMode === 'grade' && (<tr><th>Grade (MatCode)</th><th className="text-end">Trucks</th><th className="text-end">Qty</th></tr>)}
                        {expandMode === 'supplier' && (<tr><th>Supplier</th><th className="text-end">Trucks</th><th className="text-end">Qty</th></tr>)}
                        {expandMode === 'mines' && (<tr><th>Mines</th><th className="text-end">Trucks</th><th className="text-end">Qty</th></tr>)}
                      </thead>
                      <tbody>
                        {(expandMode === 'grade' ? loc.pivots.grade :
                          expandMode === 'supplier' ? loc.pivots.supplier : loc.pivots.mines
                        ).map((p, i) => (
                          <tr key={i}>
                            <td>{p.grade || p.supplier || p.mine}</td>
                            <td className="text-end">{p.trucks}</td>
                            <td className="text-end">{fmtQty(p.qty)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="list-group">
                    {loc.items.map((t, j) => (
                      <div className="list-group-item py-2" key={j} style={{ fontSize: '.85rem' }}>
                        <div className="d-flex justify-content-between">
                          <div><b>{t.truck_no}</b></div>
                          <div className="text-end"><b>{fmtQty(t.P_CHALLAN_WT)}</b> MT</div>
                        </div>
                          <div><b>{t.docnumber}</b></div>
                        <div style={{ opacity: 0.8 }}>{t.P_Item_name}</div>
                        <div className="d-flex justify-content-between" style={{ fontSize: '.78rem', opacity: 0.75 }}>
                          <div className={isTimeIncomplete(t) ? 'text-danger' : undefined}
                            title={isTimeIncomplete(t) ? 'Missing start or end time' : undefined}>
                            {fmtTime(t.unloading_start_time)}–{fmtTime(t.unloading_end_time)}
                          </div>
                          <div>{t.unloaded_by_name_bef} - {t.unloaded_by_name_aft}</div>
                        </div>
                        <div>{t.remarks_aft}</div>

                        {!!t.QC_DETAIL_SUMMARY && (
                          <div style={{ fontSize: '.78rem', color: '#7a4b00', marginTop: 2 }}>
                            <b>QC:</b> {t.QC_DETAIL_SUMMARY}
                          </div>
                        )}

                        {Number(t.qc_ded_amt || 0) > 0 && (
                          <div style={{ fontSize: '.78rem', color: '#b02a37', marginTop: 2 }}>
                            <b>QC Deduction:</b> ₹ {fmtQty(t.qc_ded_amt)}
                          </div>
                        )}

                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {rows.length === 0 && !loading && (
        <div className="text-center text-muted mt-4" style={{ fontSize: '.9rem' }}>
          No records for {inDate}.
        </div>
      )}

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
}
